/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
import React, { useRef, useState } from "react";
import { FileText, Upload, Trash2, PencilLine, Loader2, Link2 } from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "../ui/sonner";
import { fmtD } from "../../lib/date";
import { uploadChunked, UPLOAD_MAX } from "../../lib/upload";
import { showContractWarnings } from "../../lib/contractWarn";
import ContractRecognition from "./ContractRecognition";
import SignedContractPreview from "./SignedContractPreview";

/* 合同付款期明细标题栏：查看签约合同 PDF（CRM 签字版 / 上传件）· 上传（可多份，分片上传 ≤200MB）：2026-10-13 起
   2026-10-26：每份上传件作为独立转设计合同，可关联一个设计项目；已关联的凭证仍保留展示，但不能单独删除，避免项目关系悬空。
   放开给销售线（销售 / 分公司销售部经理 / B2B 销售）+ 管理员 / 销售副总，由后端 contract.canUpload 下发 ·
   逐份删除仍限管理员 / 销售副总（contract.canRemove，不随上传放开，防互删 / 误删）· 编辑付款期（canEditPlan） */
export function ContractBar({ api, leadId, contract, canEditPlan, planEditReason, planEditReasonEn, zh, onChanged, onEditPlan, onUseRecognized }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(0);
  const [preview, setPreview] = useState(null);
  const c = contract || {};
  const uploads = Array.isArray(c.uploads) ? c.uploads : (c.upload ? [c.upload] : []);
  const attach = (u) => ({ name: u?.name || u?.filename || "contract.pdf", at: u?.at, by: u?.by, fileId: u?.fileId, size: u?.size });
  const view = (u) => {
    // 主件沿用后端“最近上传件优先”规则；文件行与识别面板均在原页面预览所选合同。
    const primary = u || uploads[uploads.length - 1] || c.upload;
    // 固定当前主件，预览期间有人上传新版也不能把前后页或下载混成两个版本。
    setPreview({ fileId: primary?.fileId, filename: primary?.name || primary?.filename || `Contract_${c.contractNo || leadId}.pdf`,
      url: primary?.fileId ? `/files/${encodeURIComponent(primary.fileId)}/download` : c.pdfUrl, trigger: document.activeElement });
  };
  const upload = async (list) => {
    const files = Array.from(list || []).filter(Boolean);
    if (!files.length) return;
    const bad = files.find((f) => !/\.pdf$/i.test(f.name) && f.type !== "application/pdf");
    if (bad) { toast.error(zh ? `「${bad.name}」仅支持 PDF 文件` : `${bad.name}: PDF only`); if (fileRef.current) fileRef.current.value = ""; return; }
    const tooBig = files.find((f) => f.size > UPLOAD_MAX);
    if (tooBig) { toast.error(zh ? `「${tooBig.name}」超过 ${UPLOAD_MAX / 1024 / 1024}MB 上限` : `${tooBig.name} exceeds ${UPLOAD_MAX / 1024 / 1024}MB`); if (fileRef.current) fileRef.current.value = ""; return; }
    setBusy(true); setPct(0);
    try {
      let ok = 0;
      for (const f of files) {
        const fin = await uploadChunked(api, f, { type: "application/pdf", onPct: setPct });
        const { data } = await api.post(`/crm/signed/${leadId}/contract-file`, { fileId: fin.fileId, filename: fin.filename || f.name, size: fin.size });
        // 门牌软校验（只提示不拦截）：文件名门牌 ≠ 客户地址门牌时给黄色告警
        showContractWarnings(data?.warnings, zh);
        // 上传与后台条款识别独立：排队失败仍保留合同，并给出可重试提示。
        if (data?.recognitionError) toast.warning(data.recognitionError);
        // 合同已保存但提醒未完成时，不把上传误报成失败，也不宣称财务已收到通知。
        if (data?.notificationWarning) toast.warning(zh ? "合同已保存，财务提醒未完成，请联系财务核对账目。" : "Contract saved, but the finance reminder was not completed. Please contact Finance to reconcile the accounts.");
        ok += 1;
      }
      if (ok) toast.success(zh ? `已上传 ${ok} 份签约合同 PDF` : `${ok} contract PDF(s) uploaded`);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false); setPct(0);
    if (fileRef.current) fileRef.current.value = "";
  };
  const remove = async (u) => {
    if (!window.confirm(zh ? `删除签约合同 PDF「${attach(u).name}」？` : `Remove "${attach(u).name}"?`)) return;
    setBusy(true);
    try { await api.delete(`/crm/signed/${leadId}/contract-file`, { params: { fileId: u?.fileId } }); toast.success(zh ? "已删除" : "Removed"); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };
  return (
    <div className="space-y-2" data-testid="signed-contract-bar" data-source={c.source || ""} data-count={uploads.length}>
      <div className="flex flex-wrap items-center gap-2">
        {c.pdfUrl ? (
          <Button size="sm" variant="outline" onClick={() => view(null)} data-testid="signed-contract-view" className="border-brand-200 text-brand-700 hover:bg-brand-50">
            <FileText className="h-3.5 w-3.5 mr-1" />{zh ? "查看签约合同" : "View signed contract"}{c.source === "crm" && c.contractNo ? ` · ${c.contractNo}` : ""}
          </Button>
        ) : (
          <span className="text-xs text-slate-400 inline-flex items-center gap-1" data-testid="signed-contract-none"><FileText className="h-3.5 w-3.5" />{zh ? "尚无签约合同 PDF" : "No contract PDF yet"}</span>
        )}
        {c.source === "crm" && <span className="text-[11px] text-slate-400" data-testid="signed-contract-crm-hint">{zh ? "CRM 签字版自动生成；可另存上传件（多份）" : "CRM signed copy; uploads kept separately"}</span>}
        {c.canUpload && (
          <>
            <input ref={fileRef} type="file" accept="application/pdf,.pdf" multiple className="hidden" onChange={(e) => upload(e.target.files)} data-testid="signed-contract-file-input" />
            <Button size="sm" variant="outline" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="signed-contract-upload">
              {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1" />}
              {busy ? (pct ? (zh ? `上传中 ${pct}%…` : `Uploading ${pct}%…`) : (zh ? "上传中…" : "Uploading…")) : (uploads.length ? (zh ? "继续上传合同 PDF" : "Add contract PDF") : (zh ? "上传签约合同 PDF" : "Upload contract PDF"))}
            </Button>
          </>
        )}
        {canEditPlan && <span className="ml-auto inline-flex flex-col items-end gap-1"><Button size="sm" disabled={!!planEditReason} onClick={onEditPlan} className="bg-red-600 hover:bg-red-700 text-white" data-testid="signed-plan-edit"><PencilLine className="h-3.5 w-3.5 mr-1" />{zh ? "编辑付款期" : "Edit schedule"}</Button>{planEditReason && <span className="text-xs text-slate-500" data-testid="signed-plan-edit-reason">{zh ? planEditReason : (planEditReasonEn || planEditReason)}</span>}</span>}
      </div>
      {uploads.map((u, i) => (
        <div key={u.fileId || i} className="flex-wrap md:flex-nowrap flex items-center gap-2 text-xs rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1.5" data-testid={`signed-contract-item-${i}`} data-file-id={u.fileId || ""}>
          <FileText className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />
          <button type="button" onClick={() => view(u)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-slate-700 hover:text-brand-500 truncate flex-1 text-left" data-testid={`signed-contract-item-view-${i}`} title={attach(u).name}>{attach(u).name}</button>
          <span className="text-[11px] text-slate-400 whitespace-nowrap" data-testid={`signed-contract-upload-meta-${i}`}>{attach(u).at ? fmtD(attach(u).at) : ""}{attach(u).by ? ` · ${attach(u).by}` : ""}</span>
          {u.designProjectId && <span className="inline-flex items-center gap-1 whitespace-nowrap rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700" data-testid={`signed-contract-project-${i}`}><Link2 className="h-3 w-3" />{zh ? `已关联设计项目 ${u.designProjectNo || ""}` : `Design project ${u.designProjectNo || "linked"}`}</span>}
          <ContractRecognition api={api} leadId={leadId} file={u} canRecognize={!!c.canUpload} canEditPlan={canEditPlan} zh={zh} onChanged={onChanged} onEditPlan={onEditPlan} onUseRecognized={onUseRecognized} onView={() => view(u)} />
          {c.canRemove && !u.designProjectId && <Button size="sm" variant="ghost" disabled={busy} onClick={() => remove(u)} className="h-6 px-1.5 text-slate-500 hover:text-red-600" data-testid={`signed-contract-remove-${i}`} title={zh ? "删除这一份" : "Remove"}><Trash2 className="h-3 w-3" /></Button>}
        </div>
      ))}
      {preview && <SignedContractPreview api={api} leadId={leadId} preview={preview} zh={zh} onClose={() => setPreview(null)} />}
    </div>
  );
}

export { PaymentPlanDialog } from "./PaymentPlanDialog";
